// Tariff-provenance badges for the admin calls panel (history + detail).
// Derivation is the shared pure function; this file only maps categories to
// their Italian label + light-surface badge colors and renders them.
import {
  deriveCallTariffProvenance,
  type CallTariffCategory,
  type CallTariffInput,
  type CallTariffSegment,
  type CallTariffSummaryItem,
} from '@klaro7/shared-types';

const CATEGORY_META: Record<CallTariffCategory, { label: string; color: string }> = {
  // 7€ welcome bonus / free minutes (bonus-funded portion).
  bonus: { label: 'Bonus 7', color: 'text-emerald-700 bg-emerald-50' },
  // "Prima ricarica" promo rate (0,99 €/min).
  promo: { label: 'Prima ricarica', color: 'text-orange-700 bg-orange-50' },
  // Consultant's own standard rate.
  standard: { label: 'Tariffa consulente', color: 'text-neutral-700 bg-neutral-100' },
};

export function formatRatePerMinute(rate: number): string {
  return `${new Intl.NumberFormat('it-IT', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(rate)} €/min`;
}

function formatEuro(amount: number): string {
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(amount);
}

export function tariffCategoryLabel(category: CallTariffCategory): string {
  return CATEGORY_META[category].label;
}

/**
 * Compact badges for a table cell: one pill per provenance segment (label only).
 * Renders an em dash when nothing was billed.
 */
export function TariffBadges({ segments }: { segments: CallTariffSegment[] }) {
  if (segments.length === 0) return <span className="text-neutral-400">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {segments.map((s, i) => {
        const meta = CATEGORY_META[s.category];
        return (
          <span
            key={i}
            className={`inline-block whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ${meta.color}`}
            title={`${meta.label} · ${formatRatePerMinute(s.ratePerMinute)}`}
          >
            {meta.label}
          </span>
        );
      })}
    </div>
  );
}

/** Same as TariffBadges but derives from a raw session's billing fields. */
export function SessionTariffBadges({ session }: { session: CallTariffInput }) {
  return <TariffBadges segments={deriveCallTariffProvenance(session).segments} />;
}

function formatMinutes(minutes: number): string {
  return `${new Intl.NumberFormat('it-IT', { maximumFractionDigits: 1 }).format(minutes)} min`;
}

/**
 * Per-category usage totals (count · minutes · importo) across the whole
 * filtered history — e.g. how much was used at "Prima ricarica" vs at the
 * consultant's standard rate. Shown above the call-history table.
 */
export function TariffSummaryBar({ summary }: { summary: CallTariffSummaryItem[] }) {
  if (summary.length === 0) return null;
  const order: CallTariffCategory[] = ['bonus', 'promo', 'standard'];
  const sorted = [...summary].sort((a, b) => order.indexOf(a.category) - order.indexOf(b.category));
  return (
    <div className="mb-3 flex flex-wrap gap-2">
      {sorted.map((item) => {
        const meta = CATEGORY_META[item.category];
        return (
          <div
            key={item.category}
            className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium ${meta.color}`}
          >
            <span>{meta.label}</span>
            <span className="opacity-70">
              · {item.count} {item.count === 1 ? 'chiamata' : 'chiamate'} · {formatMinutes(item.minutes)} ·{' '}
              {formatEuro(item.amount)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Detailed provenance: each segment on its own row with the applied rate and the
 * billed amount from that source. Used in the call-detail "Importi" card.
 */
export function TariffProvenanceDetail({ segments }: { segments: CallTariffSegment[] }) {
  if (segments.length === 0) return <span className="text-neutral-400">—</span>;
  return (
    <div className="flex flex-col items-end gap-1.5">
      {segments.map((s, i) => {
        const meta = CATEGORY_META[s.category];
        return (
          <div key={i} className="flex items-center justify-end gap-2">
            <span
              className={`inline-block whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ${meta.color}`}
            >
              {meta.label}
            </span>
            <span className="whitespace-nowrap text-xs text-neutral-500">
              {formatRatePerMinute(s.ratePerMinute)}
              {segments.length > 1 ? ` · ${formatEuro(s.amount)}` : ''}
            </span>
          </div>
        );
      })}
    </div>
  );
}
